<script lang="ts">
    import { link } from 'svelte-spa-router';
    import { marketingUrl } from '../../../js/marketing';

    export let title: string = '';
    export let subtitle: string = '';
    export let showMarketingLink: boolean = true;
</script>

<div class="auth-layout">
    <div class="auth-backdrop" aria-hidden="true"></div>

    <header class="auth-header">
        <a use:link href="/" class="logo-link" aria-label="Xplorit home">
            <img src="./src/assets/xplorit_color.png" alt="Xplorit" class="logo-img" />
        </a>
        {#if showMarketingLink}
            <a
                href={marketingUrl('/')}
                target="_blank"
                rel="noopener noreferrer"
                class="marketing-button"
            >
                <i class="external alternate icon" aria-hidden="true"></i>
                <span>xplorit.io</span>
            </a>
        {/if}
    </header>

    <main class="auth-main">
        <div class="auth-card">
            <div class="auth-card-accent" aria-hidden="true"></div>
            {#if title}
                <header class="auth-card-header">
                    <h1 class="auth-title">{title}</h1>
                    {#if subtitle}<p class="auth-subtitle">{subtitle}</p>{/if}
                </header>
            {/if}
            <div class="auth-body">
                <slot />
            </div>
            <footer class="auth-card-footer">
                <slot name="footer" />
            </footer>
        </div>
    </main>
</div>

<style>
    .auth-layout {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        background: var(--surface-sunken);
        position: relative;
    }

    .auth-backdrop {
        position: absolute;
        inset: 0;
        pointer-events: none;
        background:
            radial-gradient(ellipse 50% 40% at 12% 18%, var(--accent-teal-soft) 0%, transparent 70%),
            radial-gradient(ellipse 45% 35% at 88% 82%, var(--accent-orange-soft) 0%, transparent 65%),
            radial-gradient(ellipse 35% 30% at 75% 12%, var(--accent-pink-soft) 0%, transparent 60%);
        opacity: 0.55;
    }

    .auth-header {
        position: relative;
        z-index: 1;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--space-5) var(--space-6);
        max-width: 1200px;
        width: 100%;
        margin: 0 auto;
        box-sizing: border-box;
    }

    .logo-img { height: 32px; width: auto; }

    .logo-link:focus-visible,
    .marketing-button:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
        border-radius: var(--radius-cta);
    }

    .marketing-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-4);
        font: inherit;
        font-size: var(--text-sm);
        font-weight: 500;
        color: var(--grey);
        text-decoration: none;
        background: var(--surface);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-cta);
        box-shadow: var(--shadow-card);
        cursor: pointer;
        transition: color 0.15s, border-color 0.15s;
    }

    .marketing-button:hover {
        color: var(--primary);
        border-color: var(--primary);
    }

    .auth-main {
        position: relative;
        z-index: 1;
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-5);
    }

    .auth-card {
        position: relative;
        background: var(--surface);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-pop);
        padding: var(--space-8);
        width: 100%;
        max-width: 420px;
        animation: auth-card-enter 0.45s ease-out both;
    }

    @keyframes auth-card-enter {
        from {
            opacity: 0;
            transform: translateY(12px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .auth-card-accent {
        position: absolute;
        top: 0;
        left: var(--space-6);
        right: var(--space-6);
        height: 3px;
        border-radius: 0 0 var(--radius-pill) var(--radius-pill);
        background: linear-gradient(
            90deg,
            var(--accent-teal),
            var(--accent-orange),
            var(--accent-pink),
            var(--accent-purple)
        );
    }

    .auth-card-header {
        margin-bottom: var(--space-6);
    }

    .auth-title {
        font-size: var(--text-2xl);
        font-weight: 500;
        color: var(--grey);
        margin: 0 0 var(--space-2) 0;
        text-wrap: balance;
        line-height: 1.25;
    }

    .auth-subtitle {
        font-size: var(--text-base);
        color: var(--grey-secondary);
        margin: 0;
        line-height: 1.5;
    }

    .auth-card-footer:empty { display: none; }
    .auth-card-footer {
        margin-top: var(--space-6);
        padding-top: var(--space-4);
        border-top: 1px solid var(--border-color);
        font-size: var(--text-sm);
        color: var(--grey-secondary);
    }

    @media (max-width: 480px) {
        .auth-header { padding: var(--space-4); }
        .auth-main { padding: var(--space-4); }
        .auth-card { padding: var(--space-5); }
        .auth-card-accent { left: var(--space-5); right: var(--space-5); }
    }
</style>
